import { Component, computed, ElementRef, EventEmitter, inject, input, model, Output } from '@angular/core';
import { AuiIcon, AuiIconComponent, AuiIconWeight } from '@ral/ui:aui/icons';

import { AuiInputGroupComponent } from '@ral/ui:aui/input-group';
import { AuiButtonStyleType, AuiColor, AuiSizeType } from '@ral/utils:core/ui';
import { hostBinding } from 'ngxtension/host-binding';

/**
 * @deprecated Use `auiItem` directive instead. This component will be removed in a future version.
 *
 * Example migration:
 * ```html
 * <!-- Old -->
 * <aui-button [label]="'Click me'" [color]="'primary'"></aui-button>
 *
 * <!-- New -->
 * <button auiItem [color]="'primary'">Click me</button>
 * ```
 */
@Component({
    selector: 'aui-button',
    //changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AuiIconComponent],
    template: `
        <button
            [type]="type()"
            [disabled]="disabledState()"
            (click)="clicked.emit()"
            (focus)="focused.emit()"
            (blur)="blured.emit()"
            [class]="calculatedHostClasses()"
            [autofocus]="autofocus()"
        >
            @if (icon(); as icon) {
                <div class="flex-none">
                    <aui-icon [icon]="icon" [fixedWidth]="true" [weight]="iconWeight()" />
                </div>
            }
            @if (label(); as label) {
                <div class="min-w-0 flex-shrink px-1">{{ label }}</div>
            }
            @if (rightIcon(); as rightIcon) {
                <div class="flex-none">
                    <aui-icon [icon]="rightIcon" [fixedWidth]="true" [weight]="iconWeight()" />
                </div>
            }
        </button>
    `,
})
export class AuiButtonComponent {
    /**
     * @deprecated This component is deprecated. Use `auiItem` directive instead.
     */
    @Output() clicked = new EventEmitter<Event>();
    @Output() focused = new EventEmitter<Event>();
    // TODO: Better event name???
    @Output() blured = new EventEmitter<FocusEvent>();

    inputGroupComponent = inject(AuiInputGroupComponent, { optional: true });

    elementRef = inject(ElementRef);

    private host = inject(ElementRef);

    isDirectParent(): boolean {
        if (!this.inputGroupComponent) return false;

        const parent = this.host.nativeElement.parentElement.parentElement;
        return parent === this.inputGroupComponent.el.nativeElement;
    }

    iconWeight = input<AuiIconWeight>('far');
    type = input<'button' | 'submit'>('button');
    icon = input<AuiIcon | null>(null);
    rightIcon = input<AuiIcon | null>(null);
    size = model<AuiSizeType | null | undefined>();
    color = model<AuiColor | null | undefined>();
    label = input<string | null | undefined>(null);
    buttonStyle = model<AuiButtonStyleType | null | undefined>();
    rounded = model<boolean>(false);
    disabled = model<boolean | undefined>(false);
    loading = model<boolean | undefined>(false);
    disabledState = computed(() => this.disabled() || this.loading());
    labelUnder = input<boolean>(false);
    autofocus = input<boolean>(false);

    // TODO: rename to fullWidth
    // make button full width
    fullWidth = model<boolean>(false);
    fullHeight = model<boolean>(false);

    calculatedHostClasses = computed(() => {
        const fullWidth = this.fullWidth();
        const fullHeight = this.fullHeight();
        const size = this.size() ?? '28';
        const color = this.color() ?? 'gray';
        const buttonStyle = this.buttonStyle() ?? 'solid';
        const label = this.label();
        const disabled = this.disabled();
        const rounded = this.rounded();
        const loading = this.loading();
        const labelUnder = this.labelUnder();

        let tailwindClasses = [
            'flex',
            'flex-nowrap',
            'justify-center',
            'gap-1.5',
            'text-center',
            'transition-all',
            'forced-color-adjust-none',
            'relative',
            'overflow-hidden',
            'items-center',
        ];

        if (this.inputGroupComponent === null) {
            tailwindClasses.push('border-solid', 'border');
        }

        const borders: Record<AuiColor, string[]> = {
            transparent: ['border-transparent', 'hover:border-transparent', 'active:border-transparent'],
            gray: ['border-gray', 'hover:border-gray-dark', 'active:border-gray-darken'],
            primary: ['border-primary', 'hover:border-primary-dark', 'active:border-primary-darken'],
            success: ['border-success', 'hover:border-success-dark', 'active:border-success-darken'],
            danger: ['border-danger', 'hover:border-danger-dark', 'active:border-danger-darken'],
            warning: ['border-warning', 'hover:border-warning-dark', 'active:border-warning-darken'],
            info: ['border-info', 'hover:border-info-dark', 'active:border-info-darken'],
            help: ['border-help', 'hover:border-help-dark', 'active:border-help-darken'],
        };

        const solidBackgrounds: Record<AuiColor, string[]> = {
            transparent: ['bg-transparent', 'hover:bg-transparent', 'active:bg-transparent'],
            gray: ['bg-gray', 'hover:bg-gray-dark', 'active:bg-gray-darken'],
            primary: ['bg-primary', 'hover:bg-primary-dark', 'active:bg-primary-darken'],
            success: ['bg-success', 'hover:bg-success-dark', 'active:bg-success-darken'],
            danger: ['bg-danger', 'hover:bg-danger-dark', 'active:bg-danger-darken'],
            warning: ['bg-warning', 'hover:bg-warning-dark', 'active:bg-warning-darken'],
            info: ['bg-info', 'hover:bg-info-dark', 'active:bg-info-darken'],
            help: ['bg-help', 'hover:bg-help-dark', 'active:bg-help-darken'],
        };

        const transparentBackgrounds: Record<AuiColor, string[]> = {
            transparent: [],
            gray: ['bg-gray/[0]', 'hover:bg-gray-dark/[.1]', 'active:bg-gray-darken/[.2]'],
            primary: ['bg-primary/[0]', 'hover:bg-primary-dark/[.1]', 'active:bg-primary-darken/[.2]'],
            success: ['bg-success/[0]', 'hover:bg-success-dark/[.1]', 'active:bg-success-darken/[.2]'],
            danger: ['bg-danger/[0]', 'hover:bg-danger-dark/[.1]', 'active:bg-danger-darken/[.2]'],
            warning: ['bg-warning/[0]', 'hover:bg-warning-dark/[.1]', 'active:bg-warning-darken/[.2]'],
            info: ['bg-info/[0]', 'hover:bg-info-dark/[.1]', 'active:bg-info-darken/[.2]'],
            help: ['bg-help/[0]', 'hover:bg-help-dark/[.1]', 'active:bg-help-darken/[.2]'],
        };

        const decentBackgrounds: Record<AuiColor, string[]> = {
            transparent: [],
            gray: ['bg-gray/[.1]', 'hover:bg-gray-dark/[.2]', 'active:bg-gray-darken/[.3]'],
            primary: ['bg-primary/[.1]', 'hover:bg-primary-dark/[.2]', 'active:bg-primary-darken/[.3]'],
            success: ['bg-success/[.1]', 'hover:bg-success-dark/[.2]', 'active:bg-success-darken/[.3]'],
            danger: ['bg-danger/[.1]', 'hover:bg-danger-dark/[.2]', 'active:bg-danger-darken/[.3]'],
            warning: ['bg-warning/[.1]', 'hover:bg-warning-dark/[.2]', 'active:bg-warning-darken/[.3]'],
            info: ['bg-info/[.1]', 'hover:bg-info-dark/[.2]', 'active:bg-info-darken/[.3]'],
            help: ['bg-help/[.1]', 'hover:bg-help-dark/[.2]', 'active:bg-help-darken/[.3]'],
        };

        const reverseText: Record<AuiColor, string[]> = {
            transparent: [],
            gray: ['text-gray-text'],
            primary: ['text-primary-text'],
            success: ['text-success-text'],
            danger: ['text-danger-text'],
            warning: ['text-warning-text'],
            info: ['text-info-text'],
            help: ['text-help-text'],
        };

        const coloredText: Record<AuiColor, string[]> = {
            transparent: ['text-inherit', 'hover:text-inherit', 'active:text-inherit'],
            gray: ['text-gray', 'hover:text-gray', 'active:text-gray'],
            primary: ['text-primary', 'hover:text-primary-dark', 'active:text-primary-darken'],
            success: ['text-success', 'hover:text-success-dark', 'active:text-success-darken'],
            danger: ['text-danger', 'hover:text-danger-dark', 'active:text-danger-darken'],
            warning: ['text-warning', 'hover:text-warning-dark', 'active:text-warning-darken'],
            info: ['text-info', 'hover:text-info-dark', 'active:text-info-darken'],
            help: ['text-help', 'hover:text-help-dark', 'active:text-help-darken'],
        };

        const focusRing: Record<AuiColor, string[]> = {
            transparent: [],
            gray: ['ring-gray'],
            primary: ['ring-primary'],
            success: ['ring-success'],
            danger: ['ring-danger'],
            warning: ['ring-warning'],
            info: ['ring-info'],
            help: ['ring-help'],
        };

        const loadingColor: Record<AuiColor, string[]> = {
            transparent: ['before:via-surface-background-darker'],
            gray: ['before:via-gray-dark/[.8]'],
            primary: ['before:via-primary-dark/[.8]'],
            success: ['before:via-success-dark/[.8]'],
            danger: ['before:via-danger-dark/[.8]'],
            warning: ['before:via-warning-dark/[.8]'],
            info: ['before:via-info-dark/[.8]'],
            help: ['before:via-help-dark/[.8]'],
        };

        const squareWidth: Record<AuiSizeType, string> = {
            '20': 'w-5',
            '24': 'w-6',
            '28': 'w-7',
            '32': 'w-8',
            '40': 'w-10',
            '48': 'w-12',
        };

        tailwindClasses.push(fullWidth ? 'block w-full' : 'inline-block');
        tailwindClasses.push(labelUnder ? 'flex-col' : 'flex-row');

        // compute y paddings
        if (size === '48') {
            tailwindClasses.push('py-[13px] aui-font-regular-16');
        }
        if (size === '40') {
            tailwindClasses.push('py-[8px] aui-font-regular-16');
        }
        if (size === '32') {
            tailwindClasses.push('py-[5px] aui-font-regular-16');
        }
        if (size === '28') {
            tailwindClasses.push('py-[3px] aui-font-regular-16');
        }
        if (size === '24') {
            tailwindClasses.push('py-[1px] aui-font-regular-16');
        }
        if (size === '20') {
            tailwindClasses.push('py-[3px] aui-font-regular-10');
        }

        // icon only or with label
        if (!label) {
            tailwindClasses.push(squareWidth[size]);
        } else {
            if (size === '48') {
                tailwindClasses.push('px-6 aui-font-regular-20');
            } else if (size === '40') {
                tailwindClasses.push('px-4 aui-font-regular-18');
            } else {
                tailwindClasses.push('px-2');
            }
        }

        // rounded
        // TODO: groups
        if (this.inputGroupComponent === null || !this.isDirectParent()) {
            tailwindClasses.push(rounded ? 'rounded-full' : 'rounded');
        }

        // colors

        if (buttonStyle === 'solid') {
            tailwindClasses.push(...borders[color]);
            tailwindClasses.push(...solidBackgrounds[color]);
            tailwindClasses.push(...reverseText[color]);
        }
        if (buttonStyle === 'outline') {
            tailwindClasses.push(...borders[color]);
            tailwindClasses.push(...solidBackgrounds['transparent']);
            tailwindClasses.push(...coloredText[color]);
        }
        if (buttonStyle === 'decent') {
            //const decentBackground = [...decentBackgrounds[color]];
            tailwindClasses.push(...borders['transparent']);
            tailwindClasses.push(...decentBackgrounds[color]);
            tailwindClasses.push(...coloredText[color]);
        }
        if (buttonStyle === 'text') {
            tailwindClasses.push(...borders['transparent']);
            tailwindClasses.push(...transparentBackgrounds[color]);
            tailwindClasses.push(...coloredText[color]);
        }

        // ring
        tailwindClasses.push(...focusRing[color]);

        // disabled opacity + remove active, focus and hover states

        if (disabled || loading) {
            tailwindClasses.push('opacity-70');
            tailwindClasses = tailwindClasses.filter(
                (p) => !(p.includes('hover') || p.includes('active') || p.includes('focus'))
            );
        }

        if (disabled) {
            tailwindClasses.push('cursor-not-allowed');
        }

        if (loading) {
            tailwindClasses.push(
                ...loadingColor[color],
                'cursor-wait',
                "before:content-[''] before:absolute before:top-0 before:-left-0 before:-bottom-0 before:w-[200%] before:from-transparent before:to-transparent before:via-50% before:z-10 before:bg-gradient-to-r before:animate-leftRight"
            );
        }

        if (fullHeight || this.inputGroupComponent !== null) {
            tailwindClasses.push('h-full');
        }

        return tailwindClasses.join(' ');
    });
    classes = hostBinding(
        'attr.class',
        computed(() => {
            if (this.fullWidth()) {
                return 'w-full';
            }
            return 'max-w-fit';
        })
    );
}
